import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/vue-query";
import { computed, type Ref, unref } from "vue";
import { resolveDefault } from "@/dynamic/default-token";
import { CREATE_DEFAULT_PREFIX, RETURN_PARAM } from "@/dynamic/resolvers/query-defaults";
import type { DynamicRecord, RecordFilter, SortSpec } from "@/shared/contracts/structure";
import { request } from "@/shared/http/client";
import type { RunSummary } from "@/shared/i18n/describe-error";

/**
 * CRUD for any dynamic entity, through one hook.
 *
 * Note there is no per-entity hook and no generated client: the entity code is a
 * parameter. Adding an entity adds nothing here.
 */
export type { DynamicRecord } from "@/shared/contracts/structure";

/**
 * Everything that narrows a list, in one object.
 *
 * `filters` is deliberately the SAME shape a view saves in `filter_spec`, so a
 * saved list and a chip somebody just added are the same thing to everything
 * downstream — one grammar, not two.
 */
export interface RecordQuery {
	readonly contact?: string;
	readonly search?: string;
	readonly limit?: number;
	readonly offset?: number;
	readonly sort?: SortSpec | null;
	readonly filters?: readonly RecordFilter[];
}

export const recordKeys = {
	/**
	 * The key carries the WHOLE query.
	 *
	 * It used to carry only `search`, which meant page two was served the cached
	 * page one: same key, different rows, and nothing on screen to say so. Any
	 * parameter that changes the answer has to change the key.
	 */
	list: (entity: string, query: RecordQuery) => ["records", entity, "list", query] as const,
	detail: (entity: string, id: string) => ["records", entity, id] as const,
};

/** The querystring the record API takes. Empty parts are simply absent. */
export function recordQueryString(query: RecordQuery): string {
	const params = new URLSearchParams();
	if (query.contact) params.set("contact", query.contact);
	if (query.search) params.set("search", query.search);
	if (query.limit !== undefined) params.set("limit", String(query.limit));
	if (query.offset) params.set("offset", String(query.offset));
	if (query.sort) {
		params.set("sort", query.sort.field);
		params.set("dir", query.sort.dir);
	}
	// One JSON parameter rather than `filter.<field>=` repeated: the value may be
	// a list or a range, and a querystring that has to encode those grows a
	// grammar of its own.
	//
	// A LIST of `{field, op, value}`, which is the grammar `listRecordsSchema`
	// takes and the one a view's `filterSpec` stores. It used to be an object
	// keyed by field code, which zod refused — every chip on the entity screen
	// answered 400 while the build stayed green.
	const filters = query.filters ?? [];
	if (filters.length > 0) params.set("filter", JSON.stringify(filters));
	const text = params.toString();
	return text ? `?${text}` : "";
}

/** One page of a screen's records — the read `useRecords` caches, for a one-off lookup. */
export function listRecords(
	entity: string,
	query: RecordQuery,
): Promise<{ items: DynamicRecord[]; total: number }> {
	return request(`/v3/records/${entity}${recordQueryString(query)}`);
}

export function useRecords(entity: Ref<string> | string, query: Ref<RecordQuery> | RecordQuery) {
	const entityCode = computed(() => unref(entity));
	const current = computed(() => unref(query));

	return useQuery({
		queryKey: computed(() => recordKeys.list(entityCode.value, current.value)),
		queryFn: () => listRecords(entityCode.value, current.value),
		enabled: computed(() => entityCode.value.length > 0),
		// The previous page stays on screen while the next one loads, so paging does
		// not flash an empty table between two full ones.
		placeholderData: (previous) => previous,
	});
}

/** One record, for a screen that is about exactly one. */
export function useRecord(entity: Ref<string> | string, id: Ref<string> | string) {
	const entityCode = computed(() => unref(entity));
	const recordId = computed(() => unref(id));

	return useQuery({
		queryKey: computed(() => recordKeys.detail(entityCode.value, recordId.value)),
		// From the key, not the refs: two screens share one record's cache, and a refetch
		// run with the other's closure read its id after it had emptied — the LIST landed
		// in the record's cache and the page went blank after a stage-gate save (R3).
		queryFn: ({ queryKey: [, entity, id] }) => getRecord(entity, id),
		enabled: computed(() => entityCode.value.length > 0 && recordId.value.length > 0),
	});
}

export function useCreateRecord(entity: Ref<string> | string) {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (values: Record<string, unknown>): Promise<DynamicRecord> =>
			request(`/v3/records/${unref(entity)}`, {
				method: "POST",
				body: values,
			}),
		// Um registro de uma tabela com funil É um cartão: o quadro relê sempre (o
		// "Novo cartão" cria por cima dele). E TODO registro, não só os desta tabela:
		// o servidor recalcula o pai (saldo, total) e o que um vínculo aponta, e o
		// próximo copyFrom leria do cache o saldo de antes (o 2º recebimento parcial, P6).
		onSuccess: () => {
			void client.invalidateQueries({ queryKey: ["board"] });
			return client.invalidateQueries({ queryKey: ["records"] });
		},
	});
}

export function useUpdateRecord(entity: Ref<string> | string) {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (input: { id: string; values: Record<string, unknown> }) =>
			request<DynamicRecord>(`/v3/records/${unref(entity)}/${input.id}`, {
				method: "PATCH",
				body: input.values,
			}),
		// Todo registro: ver `useCreateRecord` — editar um filho muda o pai.
		onSuccess: () => {
			void client.invalidateQueries({ queryKey: ["records"] });
			void client.invalidateQueries({ queryKey: ["board"] });
		},
	});
}

/**
 * What a row can DO, as the backend declares it (§3.10).
 *
 * Declared here and not in `shared/contracts/structure` because nothing outside
 * this feature reads it: `dynamic/` may not know a feature exists, so the table
 * and the form never see an action — the page slots the buttons in. The shape
 * mirrors `action_def` (migration 0060) exactly, so there is one contract written
 * twice and not two contracts.
 */
/**
 * UM BOTÃO — que é uma regra de automação com a resposta "quando alguém apertar".
 *
 * Era `action_def`: uma terceira tabela guardando o que correr, sob que condição
 * e com que parâmetros — as mesmas três coisas que uma regra já guardava. Tinha
 * zero linhas em produção e nenhuma tela, porque não era um conceito: era um
 * modo de disparo escrito como se fosse.
 *
 * O que a tela perdeu com a troca, e de propósito: `condition` por linha e
 * `params`. A condição passou a ser avaliada no SERVIDOR, no mesmo despachante
 * que avalia todas as outras — antes eram dois avaliadores, um aqui e um lá, a
 * concordarem por sorte. E `params` (um botão que pergunta o que escrever) tinha
 * zero uso; volta como uma coluna no dia em que alguém pedir.
 */
export interface RecordAction {
	/** O id da regra. É o que a rota de apertar recebe. */
	readonly code: string;
	readonly label: string;
	readonly description: string | null;
	/** Pergunta antes de agir. Para o que não tem volta. */
	readonly confirm: boolean;
	/** O que ele faz, para a tela poder dizê-lo sem adivinhar. */
	readonly functionCode: string;
	/**
	 * Em vez de correr algo, abre o formulário de criação de uma tela filha já
	 * ligada a esta linha (`link`) — o botão que pergunta antes de escrever.
	 */
	readonly opens?: {
		readonly entity: string;
		readonly link: string;
		readonly defaults?: Readonly<Record<string, unknown>>;
	};
}

/** O campo da linha apertada que um default lê — `"$record.<campo>"` — ou null. */
function recordToken(value: unknown): string | null {
	return typeof value === "string" && value.startsWith("$record.") ? value.slice(8) : null;
}

/** Os campos que os `$record.<campo>` pedem e a linha carregada não trouxe — então busca-se o registro. */
export function opensMissing(
	opens: NonNullable<RecordAction["opens"]>,
	row: Readonly<Record<string, unknown>>,
): string[] {
	return Object.values(opens.defaults ?? {})
		.map(recordToken)
		.filter((code): code is string => code !== null && !(code in row));
}

/**
 * O endereço do formulário que um botão `opens` abre — o mesmo `?novo=1&campo.x=`
 * do "+ Novo" e de `open.createRecord`: o drawer de criação de sempre, com o
 * vínculo escolhido (e o `copyFrom` a correr nele) e os `$today/$now/$me` e
 * `$record.<campo>` (da linha apertada) já resolvidos, porque o endereço só
 * carrega valores. Vazio não vai: o copyFrom/default da filha decide.
 */
export function opensHref(
	opens: NonNullable<RecordAction["opens"]>,
	row: Readonly<Record<string, unknown>> & { readonly id: string },
	at: { readonly now: Date; readonly timeZone: string; readonly me?: string },
	/** Para onde o formulário devolve a pessoa ao fechar — a tela do registro apertado. */
	back?: string,
): string {
	const params = new URLSearchParams({ novo: "1" });
	if (back) params.set(RETURN_PARAM, back);
	params.append(`${CREATE_DEFAULT_PREFIX}${opens.link}`, row.id);
	for (const [field, raw] of Object.entries(opens.defaults ?? {})) {
		const code = recordToken(raw);
		const value = code === null ? resolveDefault(raw, at) : row[code];
		if (field === opens.link || value === null || value === undefined || value === "") continue;
		if (Array.isArray(value) && value.length === 0) continue;
		params.append(`${CREATE_DEFAULT_PREFIX}${field}`, String(value));
	}
	return `/e/${encodeURIComponent(opens.entity)}?${params.toString()}`;
}

/** Um registro, de uma vez — o que `useRecord` guarda, para quem só precisa dele agora. */
export function getRecord(entity: string, id: string): Promise<DynamicRecord> {
	return request(`/v3/records/${entity}/${id}`);
}

/**
 * O que aconteceu ao apertar — o mesmo resumo que qualquer regra devolve.
 *
 * `evaluated` e `skipped` não são detalhe técnico: um botão cuja condição não
 * bateu devolve `executed: 0`, e a tela precisa de saber a diferença entre "não
 * fez nada porque a condição disse que não" e "falhou". Era exatamente essa
 * distinção que o despachante já registava e que a porta antiga não devolvia.
 */
export interface RecordActionResult extends RunSummary {
	readonly pressed: boolean;
	readonly evaluated: number;
	readonly skipped: number;
}

/**
 * The buttons for an entity — and, given the rows on screen, which apply to each.
 *
 * `recordIds` asks the SERVER which buttons' conditions hold on each row (one
 * request per page, `applicable`): the condition has one evaluator, the
 * dispatcher's, and pressing re-checks it. Without ids, only the list.
 */
export function useRecordActions(entity: Ref<string> | string, recordIds?: Ref<readonly string[]>) {
	const entityCode = computed(() => unref(entity));
	const ids = computed(() => recordIds?.value.join(",") ?? "");
	return useQuery({
		queryKey: computed(() => ["records", entityCode.value, "buttons", ids.value] as const),
		queryFn: () => fetchButtons(entityCode.value, ids.value),
		enabled: computed(() => entityCode.value.length > 0),
	});
}

type ButtonsPage = { buttons: RecordAction[]; applicable?: Record<string, string[]> };
const fetchButtons = (entity: string, ids: string): Promise<ButtonsPage> =>
	request(
		`/v3/automation/buttons?entity=${encodeURIComponent(entity)}${ids ? `&recordIds=${ids}` : ""}`,
	);

/**
 * Os botões de MUITAS linhas — um quadro inteiro, que não tem página. De 100 em 100,
 * o teto que o servidor aceita por pedido; devolve os que valem para uma linha.
 */
export function useRecordActionsByRecord(entity: Ref<string>, recordIds: Ref<readonly string[]>) {
	const chunks = computed(() => {
		const out: string[] = [];
		for (let i = 0; i < recordIds.value.length; i += 100)
			out.push(recordIds.value.slice(i, i + 100).join(","));
		return out;
	});
	const queries = useQueries({
		queries: computed(() =>
			chunks.value.map((ids) => ({
				queryKey: ["records", entity.value, "buttons", ids],
				queryFn: () => fetchButtons(entity.value, ids),
				enabled: entity.value.length > 0,
			})),
		),
	});
	return (id: string): RecordAction[] => {
		const at = chunks.value.findIndex((ids) => ids.split(",").includes(id));
		return at < 0 ? [] : buttonsFor(queries.value[at]?.data, id);
	};
}

/** The buttons that apply to one row; all of them when the server was not asked. */
export function buttonsFor(
	data: { buttons: RecordAction[]; applicable?: Record<string, string[]> } | undefined,
	id: string,
): RecordAction[] {
	const codes = data?.applicable?.[id];
	return (data?.buttons ?? []).filter((b) => !data?.applicable || codes?.includes(b.code));
}

/**
 * Pressing one.
 *
 * Every record is invalidated afterwards and not just the row: an action runs an
 * artifact, and an artifact is SQL — "gerar parcelas" writes rows of ANOTHER
 * table, nobody asked this screen about.
 */
export function useRunRecordAction(_entity: Ref<string> | string) {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (input: { id: string; code: string }): Promise<RecordActionResult> =>
			request(`/v3/automation/buttons/${input.code}/press`, {
				method: "POST",
				body: { recordId: input.id },
			}),
		onSuccess: () => client.invalidateQueries({ queryKey: ["records"] }),
	});
}

export function useDeleteRecord(entity: Ref<string> | string) {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (id: string) =>
			request<{ deleted: boolean }>(`/v3/records/${unref(entity)}/${id}`, {
				method: "DELETE",
			}),
		onSuccess: () => client.invalidateQueries({ queryKey: ["records"] }),
	});
}

/**
 * What a bulk delete answers.
 *
 * `failed` is a LIST and not a count because the reasons differ per row — one is
 * referenced by a card, another by an invoice — and "3 falharam" leaves the person
 * with nothing to act on. A refusal on one row is not a refusal on the other 29.
 */
export interface BulkDeleteResult {
	readonly deleted: number;
	readonly failed: ReadonlyArray<{ id: string; message: string }>;
}

/**
 * Deleting a whole selection, in ONE request and with the same soft delete.
 *
 * Not a loop of `useDeleteRecord` in the browser: thirty requests fired at once is
 * thirty invalidations and a half-deleted list if the tab closes mid-way. The
 * server loops, emits one `record.deleted` per row, and says what could not go.
 */
export function useBulkDeleteRecords(entity: Ref<string> | string) {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (ids: readonly string[]): Promise<BulkDeleteResult> =>
			request(`/v3/records/${unref(entity)}/bulk-delete`, {
				method: "POST",
				body: { ids },
			}),
		// The same invalidation the single delete does: every record (a parent's total
		// moves too), and a partial failure still refreshes what did go.
		onSuccess: () => client.invalidateQueries({ queryKey: ["records"] }),
	});
}

/**
 * How many rows there are per value of one field.
 *
 * `value` is nullable on purpose: rows nobody filled the field on are a bucket
 * like any other ("sem valor"), and without it the buckets do not add up.
 */
export interface RecordCounts {
	readonly by: string;
	readonly total: number;
	readonly counts: ReadonlyArray<{ value: string | null; count: number }>;
}

/**
 * The summary above the list, counted by the SERVER.
 *
 * Counting the rows in hand would count the page — 25 of 4.000 — which is the whole
 * reason this is a request and not a `reduce`. It carries the same `search` and
 * `filter` the list is showing, so the numbers describe what is on screen.
 *
 * Paging and order are left out of the URL and of the key: they narrow nothing, and
 * a `limit` on a count would read as "count this page".
 */
export function useRecordCounts(
	entity: Ref<string> | string,
	query: Ref<RecordQuery> | RecordQuery,
	by: Ref<string> | string,
) {
	const entityCode = computed(() => unref(entity));
	const field = computed(() => unref(by));
	const narrowing = computed<RecordQuery>(() => {
		const current = unref(query);
		return { search: current.search, filters: current.filters, contact: current.contact };
	});

	return useQuery({
		// The `['records', entity]` prefix is what makes create, update, delete and
		// action invalidate the summary too, without a line written for it.
		queryKey: computed(
			() => ["records", entityCode.value, "counts", field.value, narrowing.value] as const,
		),
		queryFn: async (): Promise<RecordCounts> => {
			const narrowed = recordQueryString(narrowing.value);
			const param = `by=${encodeURIComponent(field.value)}`;
			const path = `/v3/records/${entityCode.value}/counts${narrowed ? `${narrowed}&` : "?"}${param}`;
			return request(path);
		},
		enabled: computed(() => entityCode.value.length > 0 && field.value.length > 0),
	});
}

/** A lixeira de uma tela: os apagados, mais recentes primeiro (`deleted_at` vem junto). */
export function useRecordTrash(entity: Ref<string> | string) {
	return useQuery({
		queryKey: computed(() => ["records", unref(entity), "trash"]),
		queryFn: () =>
			request<{ rows: DynamicRecord[]; total: number }>(
				`/v3/records/${unref(entity)}/trash?limit=25`,
			),
	});
}

/** Desfaz a exclusão — o servidor devolve junto os filhos que a cascata levou. */
export function useRestoreRecord(entity: Ref<string> | string) {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (id: string) =>
			request<DynamicRecord>(`/v3/records/${unref(entity)}/${id}/restore`, { method: "POST" }),
		// Invalida também os filhos: o lote volta inteiro, em outras telas.
		onSuccess: () => client.invalidateQueries({ queryKey: ["records"] }),
	});
}
